import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { api, publicApiGet, publicApiPut } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import DocEditor from '../components/drive/DocEditor';
import SheetEditor, { readSheetData } from '../components/drive/SheetEditor';
import SlidesEditor from '../components/drive/SlidesEditor';
import TextEditor from '../components/drive/TextEditor';
import OfficeShell from '../components/office/OfficeShell';
import { descargarArchivo } from '../components/office/descargas';
import { OFFICE_FILE_RE, officeKind } from '../components/office/OfficeEditor';
import { DRIVE_NAME, FILE_COLORS, OFFICE_APPS, OFFICE_NAME } from '../components/office/icons';
import '../components/drive/drive.css';
import { useStore } from '../store/useStore';
import { enDocs, saltarADocs, urlEnDocs, volverAlCorreo } from '../lib/dominios';
import { estaPublicado } from '../lib/publico';
import {
  ArrowLeft, Save, Share2, Download, Loader2, FileText, Table2, Presentation,
  Copy, Check, Building2, Globe, Lock, Users, X, MessageSquare, Eye, Pencil, Moon, Sun, Folder,
  AtSign,
} from 'lucide-react';
import { DialogoConfirmar } from '../components/drive/DialogosDrive';

/* O11 (auditoría 2026-10-09): «Abrir en el editor clásico» se guardaba en localStorage (`zuno_office_editor`, que
   además viaja a docs con la sesión) y desde entonces TODO Word o Excel abría en el editor simplificado. Ahora vale
   solo para ese archivo y esa pestaña. La preferencia vieja se ignora (no se borra). */
const PREF_CLASICO = (id) => `zuno_editor_clasico:${id}`;

function readEditorPref(id) {
  try {
    return id && sessionStorage.getItem(PREF_CLASICO(id)) === '1' ? 'classic' : 'office';
  } catch {
    return 'office';
  }
}

// Share panel roles
const ROLE_LABEL = {
  owner: 'Propietario',
  editor: 'Editor',
  commenter: 'Comentarista',
  viewer: 'Lector',
};

export function editorPathFor(file) {
  const id = file?.id || file;
  const kind = file?.kind || 'file';
  if (kind === 'doc') return `/document/${id}`;
  if (kind === 'sheet') return `/spreadsheet/${id}`;
  if (kind === 'slides') return `/presentation/${id}`;
  if (kind === 'form') return `/form/${id}`; // Orvian Forms
  return `/file/${id}`;
}

/**
 * Documento, hoja de cálculo, presentación o algo que abre Orvian Office (PDF, Visio…): se abre en
 * docs.orvian.mx. Lo demás de Drive (imágenes, texto, zip) se queda en el correo.
 * Espejo de `_es_documento()` del backend, que decide el dominio del enlace que se comparte.
 */
export function esDocumento(file) {
  const kind = file?.kind;
  return kind === 'doc' || kind === 'sheet' || kind === 'slides' || kind === 'form' || OFFICE_FILE_RE.test(file?.name || '');
}

/** A dónde lleva abrir un archivo: una ruta del portal o, si es un documento, su URL en docs. */
export function editorUrlFor(file) {
  const path = editorPathFor(file);
  return esDocumento(file) ? urlEnDocs(path) : path;
}

/** Full-page Google Docs / Sheets / Slides editor with TipTap + jspreadsheet. */
export default function DriveEditor({ mode = 'auth' }) {
  const { id, token: routeToken } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const isPublic = mode === 'public' || loc.pathname.startsWith('/open/');
  /* `esPublico` es lo contrario de `isPublic`: no es «estoy entrando por un enlace», es «este
     archivo está abierto a cualquiera con el enlace». Lo usa el dueño para verlo sin abrir nada. */
  const [enlace, setEnlace] = useState({ access: 'restricted', expires_at: null });
  const [publicoCarpeta, setPublicoCarpeta] = useState(null); // la carpeta publicada que lo contiene (D15)
  const esPublico = estaPublicado(enlace, publicoCarpeta);
  const alCambiarAcceso = (access, lk) => setEnlace({ access, expires_at: lk?.expires_at || null });
  const token = routeToken || (isPublic ? id : null);
  const fileId = isPublic ? null : id;

  const sheetHostRef = useRef(null);
  const docHtmlRef = useRef('');
  const slidesRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState('');
  const [name, setName] = useState('');
  const [docHtml, setDocHtml] = useState('');
  const [docKey, setDocKey] = useState(0);
  const [sheetRows, setSheetRows] = useState(null);
  const [sheetKey, setSheetKey] = useState(0);
  const [slides, setSlides] = useState(null);
  const [texto, setTexto] = useState('');
  const textoRef = useRef('');
  const [shareOpen, setShareOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Solo hay cambios reales si la persona tocó el editor: TipTap normaliza el HTML al cargar y dispara onChange
  const userTouchedRef = useRef(false);
  const [editorPref, setEditorPref] = useState(() => readEditorPref(id));
  // Solo los cambios de CONTENIDO se mandan al guardar: renombrar regeneraba el Word/Excel entero (O3)
  const contenidoCambiadoRef = useRef(false);
  const [officeUnavailable, setOfficeUnavailable] = useState('');
  const [meta, setMeta] = useState(null); // metadatos del archivo (el editor no necesita la vista previa convertida)
  const [saliendo, setSaliendo] = useState(false); // el archivo es del otro dominio: se está yendo allí
  const user = useStore((s) => s.user);
  const theme = useStore((s) => s.theme);
  const setTheme = useStore((s) => s.setTheme);

  // Word/Excel/PowerPoint con el editor de coedición (tiempo real en tiempo real); el editor clásico queda como alternativa
  // Por el enlace manda el backend (`office`): el invitado no elige editor, y si el motor no está
  // disponible se cae al clásico con `load({ classic: true })`.
  const officeEligible = isPublic
    ? !!preview?.office
    : (!!fileId && OFFICE_FILE_RE.test(meta?.name || preview?.name || ''));
  const useOffice = officeEligible && (isPublic || editorPref === 'office') && !officeUnavailable;
  const officeFileId = fileId || preview?.id || meta?.id || null;

  const kind = preview?.kind || (
    loc.pathname.startsWith('/spreadsheet') ? 'sheet'
      : loc.pathname.startsWith('/presentation') ? 'slides'
        : loc.pathname.startsWith('/document') ? 'doc' : 'file'
  );
  /* O3 (auditoría 2026-10-09): si Orvian Office no pudo abrir un documento, el editor clásico lo enseña SOLO
     para leer. Guardar desde el clásico regenera el archivo (estilos, imágenes, fórmulas y otras hojas se pierden)
     y el autoguardado lo hacía sin que nadie lo pidiera. */
  const clasicoSoloLectura = !!officeUnavailable && officeEligible;
  const canEdit = !!(preview?.editable ?? preview?.can_edit) && !clasicoSoloLectura;
  const avisoClasico = officeEligible && (!!officeUnavailable || editorPref === 'classic');
  const myRole = preview?.my_role || (canEdit ? 'editor' : 'viewer');

  const load = useCallback(async ({ classic = false } = {}) => {
    setBusy(true);
    // `/file/` y `/open/` pueden ser un documento (va a docs.orvian.mx) o cualquier otra cosa de
    // Drive (se queda en el correo): hasta cargarlo no se sabe. Ver lib/dominios.js.
    const ubicar = (archivo) => {
      let fuera = false;
      if (enDocs() && !esDocumento(archivo)) {
        volverAlCorreo();
        fuera = true;
      } else if (!enDocs() && esDocumento(archivo)) {
        fuera = saltarADocs(); // false si docs está apagado o si ya rebotó (entonces se abre aquí)
      }
      if (fuera) setSaliendo(true);
      return fuera;
    };
    try {
      let data;
      if (isPublic && token) {
        // `classic=1` solo cuando el motor de coedición no está disponible: entonces el backend
        // devuelve la copia convertida (que puede ir por detrás de lo que se está escribiendo).
        data = (await publicApiGet(`/drive/open/${token}${classic ? '?classic=1' : ''}`)).data;
        if (!classic && ubicar(data)) return;
        if (data.formulario) {
          // Un formulario compartido por enlace se edita en su editor (el papel lo da el enlace);
          // para RESPONDERLO está el enlace público /f/, que es otro.
          setSaliendo(true);
          window.location.replace(`/form/${data.id}`);
          return;
        }
        if (data.office) {
          setMeta(data);
          setPreview(data);
          setName(data.name || '');
          document.title = `${data.name || 'Documento'} · ${DRIVE_NAME}`;
          return;
        }
      } else if (fileId) {
        const info = (await api.get(`/drive/files/${fileId}`, { timeout: 30000 })).data;
        if (!classic && ubicar(info)) return;
        if (info.kind === 'form') {
          setSaliendo(true);
          window.location.replace(`/form/${info.id}`);
          return;
        }
        setMeta(info);
        setEnlace({ access: info.link?.access || 'restricted', expires_at: info.link?.expires_at || null });
        setPublicoCarpeta(info.publico?.por === 'carpeta' ? info.publico.carpeta : null);
        setName(info.name || '');
        if (!classic && readEditorPref(fileId) === 'office' && OFFICE_FILE_RE.test(info.name || '')) return;
        data = (await api.get(`/drive/files/${fileId}/preview`)).data;
      } else {
        throw new Error('missing id');
      }
      setPreview(data);
      setName(data.name || '');
      document.title = `${data.name || 'Documento'} · ${DRIVE_NAME}`;
      if (data.kind === 'doc') {
        const html = data.html || '<p></p>';
        setDocHtml(html);
        docHtmlRef.current = html;
        setDocKey((k) => k + 1);
      }
      if (data.kind === 'sheet') {
        setSheetRows(data.rows || [['']]);
        setSheetKey((k) => k + 1);
      }
      if (data.kind === 'text') {
        setTexto(data.text || '');
        textoRef.current = data.text || '';
      }
      if (data.kind === 'slides') {
        const sl = data.slides?.length
          ? data.slides
          : [{ title: data.name || 'Presentación', body: '', html: data.html || '<p></p>' }];
        setSlides(sl);
        slidesRef.current = sl;
      }
      setDirty(false);
      userTouchedRef.current = false;
      contenidoCambiadoRef.current = false;
    } catch (err) {
      const st = err.response?.status;
      const detail = err.response?.data?.detail
        || (err.code === 'ECONNABORTED' ? 'El servidor tardó demasiado en responder. Intenta de nuevo.' : 'No se pudo abrir el archivo');
      if (st === 401) toast.error('Inicia sesión para abrir este enlace de organización');
      else toast.error(typeof detail === 'string' ? detail : 'Error al abrir');
      setPreview({ kind: 'error', detail: typeof detail === 'string' ? detail : 'Error' });
    } finally {
      setBusy(false);
    }
  }, [fileId, isPublic, token]);

  useEffect(() => { load(); }, [load]);

  // El enlace se vigila mientras la pestaña esté abierta: si el dueño lo restringe, quien entró por
  // él deja de ver el documento sin tener que recargar (el motor ya lo echa de la sesión de edición).
  useEffect(() => {
    if (!isPublic || !token) return undefined;
    const vigilar = async () => {
      if (document.hidden) return;
      try {
        await publicApiGet(`/drive/open/${token}/estado`);
      } catch (err) {
        const st = err.response?.status;
        if (st === 403 || st === 404 || st === 410) {   // 410 = el enlace caducó (D5)
          setPreview({ kind: 'error', detail: 'El enlace ya no está disponible.' });
        }
      }
    };
    const t = setInterval(vigilar, 60000);
    return () => clearInterval(t);
  }, [isPublic, token]);

  const markDirty = () => {
    if (!userTouchedRef.current) return;
    contenidoCambiadoRef.current = true;
    setDirty(true);
  };

  const switchEditor = (pref) => {
    try {
      if (pref === 'classic') sessionStorage.setItem(PREF_CLASICO(fileId), '1');
      else sessionStorage.removeItem(PREF_CLASICO(fileId));
    } catch {
      /* sin sessionStorage: vale solo mientras esté abierta esta vista */
    }
    setEditorPref(pref);
    // Al pasar al editor de coedición se descartan los pendientes del clásico: su autoguardado no debe escribir encima
    if (pref === 'office') setDirty(false);
    setOfficeUnavailable('');
    // Al volver al clásico se recarga: el editor de coedición pudo haber guardado cambios mientras tanto
    if (pref === 'classic') load({ classic: true });
  };

  const useOfficeRef = useRef(useOffice);
  useOfficeRef.current = useOffice;

  const save = async () => {
    // Con el editor de coedición guarda el motor; guardar aquí sobrescribiría el archivo con contenido viejo
    if (!canEdit || useOfficeRef.current) return;
    setSaving(true);
    setSaveState('');
    try {
      const body = {};
      if (name && name !== preview?.name) body.name = name;
      const conContenido = contenidoCambiadoRef.current;
      if (conContenido && kind === 'doc') body.html = docHtmlRef.current || docHtml || '<p></p>';
      if (conContenido && kind === 'sheet') {
        const host = sheetHostRef.current?.querySelector?.('.gsheet-host') || sheetHostRef.current;
        body.rows = readSheetData(host, sheetRows || [['']]);
      }
      if (conContenido && kind === 'slides') body.slides = slidesRef.current || slides || [];
      if (conContenido && kind === 'text') body.text = textoRef.current;
      if (!Object.keys(body).length) {
        setDirty(false);
        return;
      }

      if (isPublic && token) {
        await publicApiPut(`/drive/open/${token}`, body);
      } else {
        await api.put(`/drive/files/${fileId}`, body);
      }
      setDirty(false);
      contenidoCambiadoRef.current = false;
      setSaveState('saved');
      toast.success('Guardado');
      setTimeout(() => setSaveState(''), 2500);
    } catch (err) {
      setSaveState('error');
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!canEdit || !dirty || useOffice) return undefined;
    const t = setTimeout(() => { save(); }, 45000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, canEdit, useOffice]);

  /* Red de seguridad del autoguardado: entre escribir y los 45 segundos hay una ventana en la que
     cerrar la pestaña se llevaba el texto sin decir nada. El navegador enseña su propio aviso; no
     se puede guardar desde aquí (una petición en `beforeunload` no llega). */
  useEffect(() => {
    if (!canEdit || !dirty || useOffice) return undefined;
    const alSalir = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', alSalir);
    return () => window.removeEventListener('beforeunload', alSalir);
  }, [dirty, canEdit, useOffice]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const download = async () => {
    try {
      if (isPublic) {
        // Por el enlace: el backend revalida el enlace y audita la descarga
        await descargarArchivo({ fileId: officeFileId, link: token, nombre: name });
        return;
      }
      const res = await api.get(`/drive/files/${fileId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'archivo';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo descargar');
    }
  };

  const KindIcon = kind === 'sheet' ? Table2 : kind === 'slides' ? Presentation : FileText;
  const accent = kind === 'sheet' ? FILE_COLORS.sheet : kind === 'slides' ? FILE_COLORS.slides : FILE_COLORS.doc;

  if ((busy && !preview) || saliendo) {
    return (
      <div className="gdocs-shell h-screen grid place-items-center" style={{ background: 'var(--gd-canvas, #eef0f7)' }}>
        <Loader2 className="h-10 w-10 animate-spin" style={{ color: accent }} />
      </div>
    );
  }

  if (useOffice && meta && officeFileId) {
    return (
      <>
        <OfficeShell
          fileId={officeFileId}
          linkToken={isPublic ? token : undefined}
          guest={isPublic}
          meta={meta}
          kind={officeKind(meta.name, meta.kind)}
          actionLink={new URLSearchParams(loc.search).get('action') || undefined}
          user={user}
          dark={theme === 'dark'}
          onShare={() => setShareOpen(true)}
          publico={esPublico && !isPublic}
          onDownload={download}
          onClassic={() => switchEditor('classic')}
          onThemeChange={setTheme}
          onUnavailable={(detail) => {
            setOfficeUnavailable(detail);
            toast.message(`${OFFICE_NAME} no pudo abrir el archivo: se muestra una vista simple de solo lectura.`);
            load({ classic: true });
          }}
          onRenamed={(newName) => {
            setMeta((m) => ({ ...m, name: newName }));
            setName(newName);
          }}
        />
        {!isPublic && (
          <DriveSharePanel
            open={shareOpen}
            onOpenChange={setShareOpen}
            fileId={fileId}
            fileName={meta.name}
            onAccesoCambiado={alCambiarAcceso}
          />
        )}
      </>
    );
  }

  if (preview?.kind === 'error') {
    return (
      <div className="gdocs-shell h-screen grid place-items-center p-8 text-center">
        <div>
          <p className="text-lg font-medium mb-2">No se pudo abrir</p>
          <p className="text-sm opacity-70 mb-4">{preview.detail}</p>
          {/* Quien llega por un enlace que ya no sirve necesita un siguiente paso, no un aviso:
              su cuenta puede tener acceso directo al archivo aunque el enlace haya muerto. */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button onClick={() => nav(isPublic ? '/login' : '/drive')}>
              {isPublic ? 'Iniciar sesión' : 'Volver a Drive'}
            </Button>
            {isPublic && (
              <Button variant="outline" onClick={() => nav('/ayuda-acceso')}>
                ¿No puedes entrar?
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gdocs-shell gdrive-root h-screen flex flex-col overflow-hidden" data-testid="drive-standalone-editor">
      <header className="gdocs-menubar shrink-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {!isPublic && (
            <button type="button" className="gdrive-icon-btn" onClick={() => nav('/drive')} aria-label="Drive">
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <div className="h-10 w-10 rounded-lg grid place-items-center shrink-0" style={{ background: `${accent}22` }}>
            <KindIcon className="h-5 w-5" style={{ color: accent }} />
          </div>
          <div className="min-w-0 flex-1">
            <input
              className="gdocs-title-input"
              value={name}
              disabled={!canEdit}
              onChange={(e) => { setName(e.target.value); setDirty(true); }}
              onBlur={() => { if (dirty && canEdit) save(); }}
            />
            <div className="gdocs-status-row">
              <span>{ROLE_LABEL[myRole] || myRole}</span>
              {isPublic && <span>· enlace público</span>}
              {esPublico && !isPublic && (
                <span style={{ color: 'var(--warn, #b26a00)', fontWeight: 600 }}>
                  · publicado en Internet{publicoCarpeta && enlace.access !== 'anyone' ? ` (por la carpeta «${publicoCarpeta}»)` : ''}
                </span>
              )}
              <span>· {OFFICE_APPS[kind]?.name || 'Vista'}</span>
              {saveState === 'saved' && <span style={{ color: 'var(--good)' }}>· Guardado</span>}
              {dirty && canEdit && <span>· Sin guardar</span>}
              {saving && <span>· Guardando…</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {officeEligible && !avisoClasico && (
            <Button
              size="sm"
              variant="ghost"
              className="rounded-[10px] h-9 px-3 text-xs"
              onClick={() => switchEditor('office')}
              style={{ color: 'var(--gd-text-sec)' }}
              data-testid="toggle-office-editor"
            >
              Abrir en {OFFICE_NAME}
            </Button>
          )}
          {canEdit && (
            <Button size="sm" className="rounded-[10px] h-9 gap-1.5 px-4" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar
            </Button>
          )}
          {!isPublic && (
            /* Publicado en Internet, el botón lo dice: antes un archivo abierto a cualquiera se
               veía EXACTAMENTE igual que uno privado y había que abrir el panel para enterarse. */
            <Button
              size="sm"
              variant="outline"
              className="rounded-[10px] h-9 gap-1"
              onClick={() => setShareOpen(true)}
              data-testid="drive-editor-share"
              style={esPublico
                ? { color: 'var(--danger)', borderColor: 'var(--danger)' }
                : { color: 'var(--gd-text)', borderColor: 'var(--gd-border-strong)' }}
            >
              {esPublico ? <Globe className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
              {esPublico ? 'Público' : 'Compartir'}
            </Button>
          )}
          <button
            type="button"
            className="gdrive-icon-btn"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            title={theme === 'dark' ? 'Tema claro' : 'Tema oscuro'}
            aria-label={theme === 'dark' ? 'Tema claro' : 'Tema oscuro'}
            data-testid="drive-editor-theme-toggle"
          >
            {theme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </button>
          <button type="button" className="gdrive-icon-btn" onClick={download} title="Descargar" aria-label="Descargar">
            <Download className="h-[18px] w-[18px]" />
          </button>
        </div>
      </header>

      {/* O3/O11 (auditoría 2026-10-09): que se vea por qué está aquí y cómo volver al editor completo */}
      {avisoClasico && (
        <div className="gdocs-aviso-clasico" role="status" data-testid="aviso-editor-clasico">
          <span>
            {clasicoSoloLectura
              ? `${OFFICE_NAME} no pudo abrir este archivo${officeUnavailable ? ` (${officeUnavailable})` : ''}. Esta es una vista simple de solo lectura para no dañar su formato.`
              : 'El editor clásico no conserva todo el formato de Word, Excel o PowerPoint. Antes de guardar aquí, el original se guarda en el historial de versiones.'}
          </span>
          <Button size="sm" className="rounded-[10px] h-8 px-3 text-xs" onClick={() => switchEditor('office')}>
            {clasicoSoloLectura ? 'Reintentar en ' : 'Volver a '}{OFFICE_NAME}
          </Button>
        </div>
      )}

      <div
        className="flex-1 min-h-0 flex flex-col overflow-hidden"
        style={{ background: 'var(--gd-canvas)' }}
        onPointerDownCapture={() => { userTouchedRef.current = true; }}
        onKeyDownCapture={() => { userTouchedRef.current = true; }}
      >
        {kind === 'doc' && (
          <DocEditor
            key={docKey}
            html={docHtml}
            editable={canEdit}
            onChange={(html) => {
              docHtmlRef.current = html;
              setDocHtml(html);
              markDirty();
            }}
          />
        )}

        {kind === 'sheet' && sheetRows && (
          <div ref={sheetHostRef} className="flex-1 min-h-0">
            <SheetEditor
              key={sheetKey}
              rows={sheetRows}
              editable={canEdit}
              onChange={(rows) => {
                setSheetRows(rows);
                markDirty();
              }}
            />
          </div>
        )}

        {kind === 'slides' && slides && (
          <SlidesEditor
            slides={slides}
            editable={canEdit}
            onChange={(s) => {
              slidesRef.current = s;
              setSlides(s);
              markDirty();
            }}
          />
        )}

        {kind === 'text' && (
          <TextEditor
            value={texto}
            readOnly={!canEdit}
            onChange={(t) => {
              textoRef.current = t;
              setTexto(t);
              markDirty();
            }}
          />
        )}

        {kind === 'pdf' && preview?.data_url && (
          <iframe title="pdf" src={preview.data_url} className="w-full h-full min-h-[80vh] border-0" />
        )}
        {kind === 'image' && preview?.data_url && (
          <div className="p-8 grid place-items-center flex-1 overflow-auto">
            <img src={preview.data_url} alt={name} className="max-w-full max-h-[80vh] rounded-lg shadow" />
          </div>
        )}
        {(kind === 'binary' || kind === 'archive' || kind === 'html' || kind === 'file') && (
          <div className="gdrive-preview-panel m-4">
            {kind === 'html' && <div dangerouslySetInnerHTML={{ __html: preview.html || '' }} />}
            {kind === 'archive' && (
              <ul className="text-sm space-y-1">
                {(preview.entries || []).map((e, i) => (
                  <li key={i} className="flex justify-between border-b py-1"><span>{e.name}</span><span>{e.size}</span></li>
                ))}
              </ul>
            )}
            {(kind === 'binary' || kind === 'file') && (
              <p>
                {preview?.demasiado_grande
                  ? 'El archivo es demasiado grande para abrirlo aquí. Descárgalo para verlo.'
                  : 'Sin editor nativo para este tipo. Usa Descargar.'}
              </p>
            )}
          </div>
        )}
      </div>

      {!isPublic && fileId && (
        <DriveSharePanel
          open={shareOpen}
          onOpenChange={setShareOpen}
          fileId={fileId}
          fileName={name}
          onAccesoCambiado={alCambiarAcceso}
        />
      )}
    </div>
  );
}

const ACCESS_OPTS = [
  { id: 'restricted', label: 'Restringido', desc: 'Solo personas con acceso', Icon: Lock },
  { id: 'org', label: 'Organización', desc: 'Cualquiera de tu organización con el enlace', Icon: Building2 },
  { id: 'domain', label: 'Mi dominio', desc: 'Solo cuentas del dominio de tu correo', Icon: AtSign },
  // Publicar en Internet no se pone al mismo nivel que lo demás: lleva su propio color y pide
  // confirmación (ver `pedirAcceso`). El backend ya aceptaba los cuatro modos; «Mi dominio» no
  // tenía forma de elegirse desde aquí y era, de hecho, código inalcanzable.
  {
    id: 'anyone',
    label: 'Cualquier persona con el enlace',
    desc: 'Público en Internet — no hace falta cuenta',
    Icon: Globe,
    peligroso: true,
  },
];

const LINK_ROLES = [
  { id: 'viewer', label: 'Lector', Icon: Eye },
  { id: 'commenter', label: 'Comentarista', Icon: MessageSquare },
  { id: 'editor', label: 'Editor', Icon: Pencil },
];

/** Cuánto vive el enlace. `null` = lo que ya tuviera; 0 = para siempre. */
const CADUCIDADES = [
  { id: 0, label: 'No caduca' },
  { id: 24, label: 'Caduca en 24 horas' },
  { id: 24 * 7, label: 'Caduca en 7 días' },
  { id: 24 * 30, label: 'Caduca en 30 días' },
];

function fechaCorta(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '';
  }
}

/**
 * Compartir un archivo o una CARPETA: el mismo diálogo y las mismas reglas (personas, organización,
 * dominio, enlace general y papel). Lo que cambia es a qué habla —`/drive/files/{id}` o
 * `/drive/carpetas/{id}`— y las palabras. Exportado para que el menú contextual de Drive use ESTE
 * panel: el diálogo de permisos es uno solo, no una copia por pantalla.
 *
 * `onAccesoCambiado` avisa al editor del acceso general que queda, para que la cabecera pueda
 * decir «Público» sin volver a pedir nada.
 */
export function DriveSharePanel({ open, onOpenChange, fileId, fileName, carpeta, onAccesoCambiado }) {
  const base = carpeta ? `/drive/carpetas/${carpeta.id}` : `/drive/files/${fileId}`;
  const listo = carpeta ? !!carpeta.id : !!fileId;
  const nombre = carpeta ? carpeta.name : fileName;
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [shares, setShares] = useState([]);
  const [heredado, setHeredado] = useState([]); // carpetas que ya dan acceso a este archivo
  /* El backend solo le da la lista de invitados al DUEÑO: a los demás les dice de quién depende el
     acceso. Antes cualquiera que pudiera abrir el archivo se llevaba los correos de todos. */
  const [soloDueno, setSoloDueno] = useState(false);
  const [dueno, setDueno] = useState('');
  const [link, setLink] = useState({ access: 'restricted', role: 'viewer', url: null });
  const [gal, setGal] = useState([]);
  // Grupos de la organización: el chip pone el grupo en el campo y «Invitar» lo comparte con él
  const [grupos, setGrupos] = useState([]);
  const [grupoElegido, setGrupoElegido] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // Confirmación pendiente: { titulo, mensaje, boton, accion }
  const [confirmar, setConfirmar] = useState(null);
  const campoUrl = useRef(null);
  const usuario = useStore((s) => s.user);

  const fullUrl = useMemo(() => {
    if (!link?.url) return '';
    // El backend da la URL ya en su dominio: docs.orvian.mx para documentos, el correo para lo demás
    if (link.url_publica) return link.url_publica;
    const origin = window.location.origin;
    return `${origin}${link.url.startsWith('/') ? link.url : `/${link.url}`}`;
  }, [link]);

  const recargarShares = useCallback(async () => {
    const sh = await api.get(`${base}/shares`);
    setShares(sh.data?.shares || []);
    setHeredado(sh.data?.carpetas || []);
    setSoloDueno(!!sh.data?.solo_dueno);
    setDueno(sh.data?.dueno || '');
  }, [base]);

  useEffect(() => {
    if (!open || !listo) return;
    (async () => {
      try {
        const [sh, lk, dir, gr] = await Promise.all([
          api.get(`${base}/shares`),
          api.get(`${base}/link`),
          api.get('/directory').catch(() => ({ data: { items: [] } })),
          api.get('/grupos').catch(() => ({ data: { grupos: [] } })),
        ]);
        setGrupos(gr.data?.grupos || []);
        setShares(sh.data?.shares || []);
        setHeredado(sh.data?.carpetas || []);
        setSoloDueno(!!sh.data?.solo_dueno);
        setDueno(sh.data?.dueno || '');
        setLink(lk.data || {});
        onAccesoCambiado?.(lk.data?.access || 'restricted', lk.data);
        setGal((dir.data?.items || []).filter((i) => i.source === 'org' && i.email).slice(0, 12));
      } catch {
        toast.error('No se pudo cargar permisos');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, listo, base]);

  const aplicarAcceso = async (access, linkRole, expiraHoras) => {
    setBusy(true);
    try {
      const cuerpo = { access, role: linkRole };
      if (expiraHoras !== undefined) cuerpo.expira_horas = expiraHoras;
      const res = await api.put(`${base}/link`, cuerpo);
      setLink(res.data);
      onAccesoCambiado?.(res.data?.access || 'restricted', res.data);
      if (access === 'restricted') {
        // El backend rota el token: hay que decirlo, porque el enlace repartido deja de servir
        toast.success(res.data?.rotado
          ? 'Enlace restringido — el enlace que compartiste dejó de funcionar'
          : 'Enlace restringido');
      } else if (access === 'anyone') {
        // Que el aviso diga lo que de verdad pasó. «Acceso general actualizado» no mencionaba que
        // el archivo acababa de quedar abierto en Internet.
        toast.success(`${carpeta ? 'La carpeta queda' : 'El archivo queda'} público en Internet`, {
          description: 'Cualquiera con el enlace puede abrirlo, sin cuenta.',
        });
      } else {
        toast.success('Acceso general actualizado');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar enlace');
    } finally {
      setBusy(false);
    }
  };

  /** Abrir a Internet se pregunta; cerrar y lo demás, no. Dar es lo que no se deshace solo. */
  const pedirAcceso = (access, linkRole = link.role || 'viewer') => {
    const yaPublico = link.access === 'anyone';
    // Pasar a Editor un enlace que YA es público concede escritura sin identificarse: también se
    // pregunta, aunque el modo de acceso no cambie.
    if (access === 'anyone' && yaPublico && linkRole === 'editor' && link.role !== 'editor') {
      setConfirmar({
        titulo: '¿Dejar editar a cualquiera?',
        mensaje: `Quien tenga el enlace podrá modificar ${carpeta ? 'lo que hay en la carpeta' : 'el documento'} `
          + 'sin cuenta y sin dar su nombre. Los cambios quedan registrados como anónimos.',
        boton: 'Permitir edición anónima',
        accion: () => aplicarAcceso(access, linkRole),
      });
      return;
    }
    if (access !== 'anyone' || yaPublico) {
      aplicarAcceso(access, linkRole);
      return;
    }
    const que = carpeta ? 'esta carpeta' : 'este archivo';
    setConfirmar({
      titulo: '¿Publicar en Internet?',
      mensaje: `Cualquier persona que tenga el enlace podrá abrir ${que} sin iniciar sesión, dentro `
        + 'y fuera de tu organización. El enlace puede reenviarse y aparecer en buscadores si alguien lo '
        + `publica. Podrás cortarlo cuando quieras con «Restringido»${linkRole === 'editor'
          ? ', pero mientras tanto quien lo tenga podrá EDITARLO sin identificarse.'
          : '.'}`,
      boton: 'Publicar en Internet',
      accion: () => aplicarAcceso(access, linkRole),
    });
  };

  const etiquetaGrupo = (g) => g.correo || g.nombre;

  const invite = async (opts = {}) => {
    const correo = (opts.email ?? email).trim();
    const grupo = !opts.scope && grupoElegido && correo === etiquetaGrupo(grupoElegido) ? grupoElegido : null;
    if (!opts.scope && !correo) {
      // Antes volvía sin decir nada: pulsar Enter con el campo vacío no hacía ni explicaba nada.
      toast.message('Escribe el correo de la persona a la que quieres dar acceso');
      return;
    }
    setBusy(true);
    try {
      const body = opts.scope
        ? { scope: opts.scope, role: opts.role || role, domain: opts.domain }
        : grupo ? { group_id: grupo.id, role: opts.role || role } : { email: correo, role: opts.role || role };
      await api.post(`${base}/share`, body);
      toast.success(opts.scope ? 'Acceso concedido' : grupo
        ? `Compartido con el grupo ${grupo.nombre} (${grupo.miembros_n} ${grupo.miembros_n === 1 ? 'persona' : 'personas'})`
        : `Compartido con ${correo}`);
      setEmail('');
      setGrupoElegido(null);
      await recargarShares();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo compartir');
    } finally {
      setBusy(false);
    }
  };

  /** Dar acceso de escritura a TODA la organización de un clic tampoco se deshace solo. */
  const invitarOrg = (papel) => {
    if (papel !== 'editor') {
      invite({ scope: 'tenant', role: papel });
      return;
    }
    setConfirmar({
      titulo: '¿Toda la organización como editor?',
      mensaje: `Todas las cuentas de tu organización podrán modificar ${carpeta ? 'esta carpeta y lo que contiene' : 'este archivo'}, `
        + 'no solo verlo. Para quitarlo después hay que buscar la fila «Organización» en la lista de accesos.',
      boton: 'Dar acceso de editor',
      accion: () => invite({ scope: 'tenant', role: 'editor' }),
    });
  };

  const cambiarPapel = async (s, papel, confirmado = false) => {
    // Subir a Editor a TODA la organización o un dominio desde el desplegable pide la misma
    // confirmación que el botón «Toda la org (editor)» (D12, auditoría 2026-10-05).
    if (!confirmado && papel === 'editor' && (s.scope === 'tenant' || s.scope === 'domain')) {
      setConfirmar({
        titulo: s.scope === 'tenant' ? '¿Toda la organización como editor?' : `¿Todo ${s.domain || 'el dominio'} como editor?`,
        mensaje: `Todas esas cuentas podrán modificar ${carpeta ? 'esta carpeta y lo que contiene' : 'este archivo'}, no solo verlo.`,
        boton: 'Dar acceso de editor',
        accion: () => cambiarPapel(s, papel, true),
      });
      return;
    }
    setBusy(true);
    try {
      // Invitar otra vez a la misma persona cambia su papel (una fila por destinatario): así se
      // puede pasar a alguien de Lector a Editor sin quitarle el acceso y volvérselo a dar.
      const body = s.group_id ? { group_id: s.group_id, role: papel }
        : s.email ? { email: s.email, role: papel } : { scope: s.scope, role: papel, domain: s.domain };
      await api.post(`${base}/share`, body);
      setShares((xs) => xs.map((x) => ((x.id || x._id) === (s.id || s._id) ? { ...x, role: papel } : x)));
      toast.success('Permiso actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cambiar el permiso');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (shareId) => {
    try {
      await api.delete(`${base}/shares/${shareId}`);
      setShares((s) => s.filter((x) => x.id !== shareId && x._id !== shareId));
      toast.success('Acceso revocado');
    } catch {
      toast.error('No se pudo revocar');
    }
  };

  const pedirRevocar = (s) => {
    const quien = s.grupo ? `El grupo ${s.grupo.nombre}` : s.email || (s.scope === 'tenant' ? 'toda la organización' : s.scope === 'domain' ? `@${s.domain}` : 'este acceso');
    setConfirmar({
      titulo: '¿Quitar el acceso?',
      mensaje: `${quien} dejará de ver ${carpeta ? 'esta carpeta' : 'este archivo'}. Puedes volver a dar acceso cuando quieras.`,
      boton: 'Quitar acceso',
      accion: () => revoke(s.id || s._id),
    });
  };

  const copyLink = async () => {
    if (!fullUrl) {
      // Este aviso antes era inalcanzable: sus dos botones estaban ocultos o deshabilitados
      // justamente cuando no había enlace, que es cuando hacía falta explicarlo.
      toast.message('Elige un acceso general para generar el enlace', {
        description: 'Con «Restringido» no hay enlace que repartir: solo entran las personas de la lista.',
      });
      return;
    }
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success('Enlace copiado');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Sin portapapeles (contexto no seguro, Safari viejo) se selecciona el enlace y se dice qué
      // hacer, en lugar de sacar el cuadro gris del navegador.
      campoUrl.current?.focus();
      campoUrl.current?.select();
      toast.message('Copia el enlace con Ctrl+C', { description: 'Ya está seleccionado.' });
    }
  };

  const accesoActual = link.access || 'restricted';
  const miDominio = `@${(usuario?.email || '').split('@')[1] || 'tu dominio'}`;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg rounded-2xl gdrive-share-dialog">
          <DialogHeader>
            <DialogTitle className="pr-6">
              {carpeta ? `Compartir la carpeta “${nombre || 'carpeta'}”` : `Compartir “${nombre || 'archivo'}”`}
            </DialogTitle>
          </DialogHeader>

          {soloDueno ? (
            <p className="text-sm" style={{ color: 'var(--gd-text-sec)' }}>
              El acceso a {carpeta ? 'esta carpeta' : 'este archivo'} lo gestiona {dueno}. Pídeselo
              a esa persona si necesitas invitar a alguien o cambiar un permiso.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="flex gap-2">
                <div className="flex-1">
                  <label htmlFor="share-email" className="sr-only">Correo de la persona a invitar</label>
                  <Input
                    id="share-email"
                    placeholder="Añadir personas o grupos (correo)"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setGrupoElegido(null); }}
                    className="rounded-xl w-full"
                    data-testid="share-email"
                    onKeyDown={(e) => { if (e.key === 'Enter') invite(); }}
                  />
                </div>
                <div>
                  <label htmlFor="share-role" className="sr-only">Permiso de la persona invitada</label>
                  <select
                    id="share-role"
                    className="gdocs-tb-select !h-10 rounded-xl border px-2"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    {LINK_ROLES.map((r) => (
                      <option key={r.id} value={r.id}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <Button className="rounded-xl" disabled={busy || !email.trim()} onClick={() => invite()}>
                  Invitar
                </Button>
              </div>

              {carpeta && (
                <p className="text-xs" style={{ color: 'var(--gd-text-sec)' }}>
                  Quien la reciba verá lo que hay dentro y en sus subcarpetas, también lo que añadas después.
                  Un editor puede además subir archivos a la carpeta.
                </p>
              )}

              {!carpeta && heredado.length > 0 && (
                <p className="text-xs flex items-start gap-1.5" style={{ color: 'var(--gd-text-sec)' }}>
                  <Folder className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    Este archivo ya se comparte con la carpeta{' '}
                    {heredado.map((c, i) => (
                      <React.Fragment key={c.id}>
                        {i > 0 && ', '}
                        <strong>{c.name}</strong>
                      </React.Fragment>
                    ))}
                    : restringir el enlace de aquí no quita ese acceso.
                  </span>
                </p>
              )}

              {grupos.length > 0 && (
                <div>
                  <div className="text-xs mb-1.5 flex items-center gap-1" style={{ color: 'var(--gd-text-sec)' }}>
                    <Users className="h-3 w-3" /> Grupos
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {/* Igual que las personas: el chip RELLENA el campo y el permiso se elige al lado */}
                    {grupos.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        className="text-xs px-2 py-1 rounded-full border hover:bg-muted"
                        title={`${g.nombre}: ${g.miembros_n} ${g.miembros_n === 1 ? 'persona' : 'personas'}. Quien entre o salga del grupo gana o pierde el acceso.`}
                        onClick={() => { setEmail(etiquetaGrupo(g)); setGrupoElegido(g); }}
                        data-testid="share-grupo"
                      >
                        {g.nombre} · {g.miembros_n}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {gal.length > 0 && (
                <div>
                  <div className="text-xs mb-1.5 flex items-center gap-1" style={{ color: 'var(--gd-text-sec)' }}>
                    <Building2 className="h-3 w-3" /> Personas de la organización
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {/* El chip RELLENA el campo, no invita: antes usaba el permiso que hubiera
                        quedado en el select sin enseñarlo junto al nombre, así que era fácil dar
                        Editor a alguien creyendo que se le daba Lector. */}
                    {gal.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className="text-xs px-2 py-1 rounded-full border hover:bg-muted"
                        title={`Poner ${p.email} en el campo de arriba`}
                        onClick={() => setEmail(p.email)}
                      >
                        {p.name || p.email}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Accesos por pertenencia: una fila en `drive_shares`, NO el acceso general por
                  enlace de abajo. Antes el tercero se llamaba «Mismo dominio @» y se confundía con
                  el modo «Mi dominio» del enlace, que es otra cosa; ahora dice a qué dominio. */}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => invitarOrg('viewer')}>
                  <Users className="h-3.5 w-3.5 mr-1" /> Toda la org (lector)
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => invitarOrg('editor')}>
                  Toda la org (editor)
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  title="Da acceso a todas las cuentas de ese dominio, tengan o no el enlace"
                  onClick={() => invite({ scope: 'domain', role: 'viewer' })}
                >
                  <AtSign className="h-3.5 w-3.5 mr-1" /> Todo {miDominio} (lector)
                </Button>
              </div>

              {shares.length > 0 && (
                <ul className="space-y-1 max-h-44 overflow-auto border rounded-xl p-2">
                  {shares.map((s) => (
                    <li key={s.id || s._id} className="flex items-center gap-2 text-sm py-1">
                      <span className="flex-1 truncate">
                        {s.group_id ? (
                          <span className="inline-flex items-center gap-1.5" title={s.grupo?.correo || ''}>
                            <Users className="h-3.5 w-3.5 shrink-0" />
                            {s.grupo ? `${s.grupo.nombre} · ${s.grupo.miembros_n} ${s.grupo.miembros_n === 1 ? 'persona' : 'personas'}` : 'Grupo eliminado'}
                          </span>
                        ) : (s.email || s.user_id || (s.scope === 'tenant' ? 'Organización' : s.scope === 'domain' ? `@${s.domain}` : '—'))}
                      </span>
                      {/* El permiso se cambia aquí mismo. Antes era texto: para pasar a alguien de
                          Lector a Editor había que quitarle el acceso y volver a invitarlo. */}
                      <label htmlFor={`share-role-${s.id || s._id}`} className="sr-only">
                        Permiso de {s.email || 'este acceso'}
                      </label>
                      <select
                        id={`share-role-${s.id || s._id}`}
                        className="gdocs-tb-select !h-8 rounded-lg border px-1.5 text-xs"
                        value={s.role || 'viewer'}
                        disabled={busy || s.role === 'owner'}
                        onChange={(e) => cambiarPapel(s, e.target.value)}
                      >
                        {LINK_ROLES.map((r) => (
                          <option key={r.id} value={r.id}>{r.label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        data-ui="button"
                        onClick={() => pedirRevocar(s)}
                        className="text-destructive grid place-items-center rounded-lg shrink-0"
                        /* 36 con ratón y 44 con el dedo (index.css, «Objetivos táctiles»). Antes
                           era un ícono de 14 px con `p-1`: unos 22 px para una acción que quita
                           un acceso, sin confirmación y sin deshacer. */
                        style={{ height: 36, width: 36 }}
                        title={`Quitar el acceso de ${s.email || 'este destinatario'}`}
                        aria-label={`Quitar el acceso de ${s.email || 'este destinatario'}`}
                        data-testid="share-revoke"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="border-t pt-3 space-y-2">
                <div className="text-sm font-medium">Acceso general</div>
                <p className="text-xs" style={{ color: 'var(--gd-text-sec)' }}>
                  {carpeta
                    ? 'Quien abra el enlace entra a la carpeta y puede abrir lo que hay dentro, siempre al día.'
                    : 'Quien abra el enlace verá el documento tal y como está en este momento, no una copia.'}
                  {shares.length > 0 && ' «Restringido» corta el enlace, pero las personas de la lista de arriba conservan su acceso.'}
                </p>
                <div className="space-y-1">
                  {ACCESS_OPTS.map((a) => {
                    const Icon = a.Icon;
                    const active = accesoActual === a.id;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        disabled={busy}
                        onClick={() => pedirAcceso(a.id, link.role || 'viewer')}
                        data-testid={`share-access-${a.id}`}
                        className={`gdrive-access-opt w-full text-left flex items-start gap-3 p-2.5 rounded-xl border ${active ? 'is-active' : ''}`}
                        style={a.peligroso && active ? { borderColor: 'var(--danger)' } : undefined}
                      >
                        <Icon
                          className="h-4 w-4 mt-0.5 shrink-0"
                          style={a.peligroso ? { color: 'var(--danger)' } : undefined}
                        />
                        <div>
                          <div className="text-sm font-medium flex items-center gap-1.5">
                            {a.label}
                            {a.peligroso && active && (
                              <span
                                className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                                style={{ background: 'var(--danger)', color: '#fff' }}
                              >
                                Público
                              </span>
                            )}
                          </div>
                          <div className="text-xs opacity-70">{a.desc}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {accesoActual !== 'restricted' && (
                  <>
                    <div className="flex items-center gap-2 pt-1">
                      <label htmlFor="link-role" className="text-xs shrink-0">Rol del enlace:</label>
                      <select
                        id="link-role"
                        className="gdocs-tb-select !h-9 rounded-lg border px-2 flex-1"
                        value={link.role || 'viewer'}
                        disabled={busy}
                        onChange={(e) => pedirAcceso(accesoActual, e.target.value)}
                      >
                        {LINK_ROLES.map((r) => (
                          <option key={r.id} value={r.id}>{r.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Un enlace que no caduca vive hasta que alguien se acuerda de volver aquí.
                        Para lo más común —mandárselo a alguien de fuera una semana— hacía falta. */}
                    <div className="flex items-center gap-2">
                      <label htmlFor="link-expira" className="text-xs shrink-0">Vigencia:</label>
                      <select
                        id="link-expira"
                        className="gdocs-tb-select !h-9 rounded-lg border px-2 flex-1"
                        value={link.expires_at ? 'puesta' : 0}
                        disabled={busy}
                        onChange={(e) => {
                          const horas = Number(e.target.value);
                          // La opción «Caduca el …» es la fecha vigente, no una orden: no se manda.
                          if (Number.isNaN(horas)) return;
                          aplicarAcceso(accesoActual, link.role || 'viewer', horas);
                        }}
                      >
                        {link.expires_at && <option value="puesta">{`Caduca el ${fechaCorta(link.expires_at)}`}</option>}
                        {CADUCIDADES.map((c) => (
                          <option key={c.id} value={c.id}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {fullUrl && (
                  <div className="flex gap-2 items-center mt-2">
                    <label htmlFor="link-url" className="sr-only">Enlace para compartir</label>
                    <Input
                      id="link-url"
                      ref={campoUrl}
                      readOnly
                      value={fullUrl}
                      onFocus={(e) => e.target.select()}
                      className="rounded-xl text-xs font-mono"
                    />
                    <Button
                      size="sm"
                      className="rounded-full shrink-0"
                      variant="outline"
                      onClick={copyLink}
                      aria-label="Copiar enlace"
                      title="Copiar enlace"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {/* Sin `disabled`: sin enlace, el botón explica cómo conseguir uno (ver `copyLink`). */}
            {!soloDueno && (
              <Button variant="outline" className="rounded-full" onClick={copyLink}>
                Copiar enlace
              </Button>
            )}
            <Button className="rounded-xl" onClick={() => onOpenChange(false)}>
              Listo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DialogoConfirmar
        open={!!confirmar}
        titulo={confirmar?.titulo || ''}
        mensaje={confirmar?.mensaje || ''}
        boton={confirmar?.boton || 'Continuar'}
        onOpenChange={(v) => !v && setConfirmar(null)}
        onConfirmar={() => { const a = confirmar?.accion; setConfirmar(null); a?.(); }}
      />
    </>
  );
}
